import React, {useMemo, useState} from 'react'
import {DataTable} from '@/components/atoms'
import {getCellDataColumns} from '@/components/organisms/DynamicColumnTable/DynamicColumnTableUtils'
import {SIZE} from '@/utils/constants/globalStyle'
import {SELECTION_TYPE} from '@/utils/constants/commonConstants'

/**
 * DynamicColumnTable component renders a table with dynamic columns and records.
 *
 * @param {Object[]} columns - Array of column definitions.
 * @param {Object[]} records - Array of record data to be displayed in the table.
 * @param {string} [pklass=''] - Optional CSS class for the table container.
 * @param {boolean} [filters=true] - Flag to enable or disable filters.
 * @param {Object} [rowSelection={}] - Object to manage row selection state.
 * @param {Function} [setRowSelection=() => {}] - Callback function to update row selection state.
 * @param {Function} [onUpdate=() => {}] - Callback function to handle updates.
 * @param {boolean} [commonCellDataCheck=false] - Flag to check for common cell data.
 * @param {boolean} [isDark=true] - The mode of the table.
 * @param {string} [tableHeader=''] - The table heading.
 * @param {string} [size='small'] - The size of the table. Options: "small" | "medium" | "large".
 * @param {string} [selection='multiple'] - The selection mode of the table.
 * @param {number} [pageSize=500] - The page size of the table.
 * @param {number} [pageIndex=0] - The page index of the table.
 * @param {string} [tableDescription=''] - The table description.
 * @param {Object[]} [toolbarActions=[]] - The toolbar actions for the table.
 * @param {string} [searchPlaceholder='Search'] - Helper text for the search input.
 * @param {boolean} [showFilterButton=false] - Prop to hide table filter option.
 * @param {boolean} [showFilterToolbar=false] - Prop to show filter toolbar with initial rendering.
 * @param {boolean} [props.showColumnFilters=true] - Prop to show column filters.
 * @param {boolean} [enableSorting=true] - Prop to enable sorting.
 * @returns {JSX.Element} The rendered DynamicColumnTable component.
 */

export const DynamicColumnTable = ({
  columns,
  records,
  pklass = '',
  filters = true,
  rowSelection = {},
  setRowSelection = () => {},
  onUpdate = () => {},
  commonCellDataCheck = false,
  isDark = true,
  tableHeader = '',
  size = SIZE.SMALL,
  selection = SELECTION_TYPE.MULTIPLE,
  pageSize = 500,
  pageIndex = 0,
  tableDescription = '',
  toolbarActions = [],
  searchPlaceholder = 'Search',
  showFilterButton = false,
  showFilterToolbar = false,
  showColumnFilters = true,
  enableSorting = true,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedRowData, setSelectedRowData] = useState([])

  const orderedColumns = useMemo(
    () => sortColumns(commonCellDataCheck ? getCellDataColumns(columns) : columns),
    [columns, commonCellDataCheck]
  )

  const {columns: orderedTableColumns, filters: orderedFilters} = useMemo(
    () =>
      columns && records ? getFilteredColumnsAndData(orderedColumns, records, !!filters) : {columns: [], filters: []},
    [orderedColumns, records, filters, columns]
  )

  if (!columns || !records) {
    return <p className="text-red-500">Columns and records are required to render the table.</p>
  }
  return (
    <div className={pklass ?? ''}>
      <DataTable
        columns={orderedTableColumns}
        data={records}
        filters={filters ? orderedFilters : []}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        onUpdate={onUpdate}
        isDark={isDark}
        tableHeader={tableHeader}
        size={size}
        selection={selection}
        pageSize={pageSize}
        pageIndex={pageIndex}
        tableDescription={tableDescription}
        toolbarActions={toolbarActions}
        searchPlaceholder={searchPlaceholder}
        showFilterButton={showFilterButton}
        showFilterToolbar={showFilterToolbar}
        setSelectedRowData={setSelectedRowData}
        showColumnFilters={showColumnFilters}
        enableSorting={enableSorting}
      />
    </div>
  )
}

// --- Helper Functions ---

function sortColumns(columns) {
  return columns?.sort((a, b) => (a.order_of_column || 0) - (b.order_of_column || 0))
}

function createColumnData(column) {
  return {
    columnKey: column.name_of_db_column,
    header: column.header || column.label_of_column,
    initialSize: column.initialSize,
    minSize: column.minSize,
    maxSize: column.maxSize,
    cellData: column.cellData,
  }
}

function generateFilterOptions(columns, records) {
  const filterableColumns = columns.filter(column => column.filterable)
  const optionsByColumn = filterableColumns.reduce((acc, column) => {
    const columnName = column.name_of_db_column

    if (!acc[columnName]) {
      acc[columnName] = new Map()
    }

    records.forEach(record => {
      const value = record[columnName]
      acc[columnName].set(value, {
        id: value,
        label: value,
      })
    })

    return acc
  }, {})
  return optionsByColumn
}

function createFilterItem(column, filterOptions) {
  const options = Array.from((filterOptions[column.name_of_db_column] || new Map()).values())
  return {
    disabled: false,
    label: column.label_of_column,
    listLabel: column.label_of_column,
    options,
    isDark: true,
    showSearch: true,
    selection: 'multiple',
    onChange: () => {},
  }
}

function getFilteredColumnsAndData(columns, records, isFilterEnabled) {
  const filterOptions = generateFilterOptions(columns, records)

  return columns.reduce(
    (acc, column) => {
      acc.columns.push(createColumnData(column))

      if (isFilterEnabled && column.filterable) {
        acc.filters.push(createFilterItem(column, filterOptions))
      }

      return acc
    },
    {columns: [], filters: []}
  )
}
